import React, {CSSProperties, FocusEventHandler, forwardRef, KeyboardEventHandler, ReactNode, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import './Select.less';
import type {ActionMeta, CSSObjectWithLabel, GetOptionLabel, GetOptionValue, GroupBase, InputActionMeta, InputProps, Options, OptionsOrGroups, StylesConfig} from 'react-select';
import ReactSelect, {createFilter, components} from 'react-select';
import CreatableSelect from 'react-select/creatable';

/** Space kept between a menu and the screen's right edge, in px (OWA-988). */
const MENU_GUTTER = 16;

// Custom Input component that prevents LastPass from injecting its icon
// Multiple attributes needed because LastPass re-scans on focus
const NoLastPassInput = (props: InputProps) => (
    <components.Input
        {...props}
        data-lpignore="true"
        data-form-type="other"
        autoComplete="new-password"
    />
);

// The menu portals into the open MUI modal that holds the Select, not the page-level
// #portal-select, which MUI marks aria-hidden while a modal is open (OWA-955).
const ModalMenuPortal = (props: React.ComponentProps<typeof components.MenuPortal>) => (
    <components.MenuPortal
        {...props}
        appendTo={props.appendTo && popupContainerFor(props.controlElement, props.appendTo)}
    />
);
import type {SelectOption, GroupedSelectOption} from "@/@types/select";
import {popupContainerFor} from "@/ui/Portal/popupContainer";
import useComponentCache from "@/hooks/useComponentCache";
import pWaitFor from "p-wait-for";
import {globalAbortSignal} from "@/lib/picos/abortController";
import useStateWithCallback from "@/hooks/useStateWithCallback";
import VirtualizedMenuList from "./VirtualizedMenuList";
import advanceFocusFrom from "@/lib/picos/focusAdvance";

/**
 * Simple multi-select checkbox component.
 *
 * Props include a list of _possible_ options and a list of _selected_ options, along with layout options.
 */
const Select = forwardRef(function Select<T, S>(
    props: SelectProps<T, S>,
    ref: React.ForwardedRef<SelectHandle<T, S>>
) {
    // Flatten grouped options for internal key/default computation
    const _options: SelectOption<T, S>[] = (props.options || []).flatMap(
        (o: any) => o.options ? o.options : [o]
    );

    if (!props.keyName && !props.name) console.warn(`No key for`, props, null, 2);
    const key = props.keyName || props.name || _options?.map(o => o.value).join('-') || 'unknown-label-select';

    // Make sure the defaultValue matches isMulti
    // if (!props.allowNone && _options.length === 0) {
    //     console.error('options must not be empty when allowNone is false');
    // }
    if (props.defaultValue && props.isMulti && !Array.isArray(props.defaultValue)) {
        // console.warn(`defaultValue for ${props.name || props.optionType || 'unknown'} must be an array when isMulti is true`);
        // props.isMulti = true;
    }
    if (props.defaultValue && !props.isMulti && Array.isArray(props.defaultValue)) {
        // console.warn(`defaultValue for ${props.name || props.optionType || 'unknown'} must not be an array when isMulti is false`);
    }

    // With no default the Select holds nothing, whether or not none is allowed (OWA-1723). A first
    // option shown here would never reach onChange, so the card would show a choice it does not hold.
    const _option = props.defaultValue ?? (props.isMulti ? [] : null);

    type SelectState = {
        hasAutofocusRun: boolean,
        // Note: isMenuOpen is intentionally NOT cached - caching it causes bugs
        // where the menu closes immediately after opening if cache has isMenuOpen: false.
        // `option` is also NOT cached - the cache key is keyed only by `name`, so
        // caching `option` cross-contaminates every <Select name='X'> instance in
        // the SPA (e.g. opening a no-lead case left option=null in `select-leadId`,
        // shadowing the next case's real lead). The parent owns the truth via
        // `defaultValue` — read it fresh on every mount.
        inputValue: string,
    }

    const {cacheSave, cacheReset, cacheValue} = useComponentCache<SelectState>(`select-${key}`);

    const [option, setOption] = useStateWithCallback<SelectOption<T, S> | SelectOption<T, S>[]>(_option);
    const [isMenuOpen, setMenuOpen] = useState<boolean>(props.menuIsOpen ?? false);
    const [inputValue, setInputValue] = useState<string>(cacheValue('inputValue', '')); // This is the search text, not the current value!
    const [hasAutofocusRun, setHasAutofocusRun] = useState<boolean>(false);
    const doResetRef = useRef<boolean>(false);

    const {
        allowCreate = false,
        allowNone,
        allowNoneText,
        autoAdvance = false,
        autoFocus,
        backspaceRemovesValue = true,
        clsName,
        components,
        controlled = false,
        defaultValue,
        disabled = false,
        encrypted = false,
        filterOption,
        formatCreateLabel,
        getNewOptionData,
        isClearable = true,
        isMulti = false,
        isValidNewOption,
        keyName,
        menuPlacement = 'auto',
        onChange,
        onCreateOption,
        onMenuOpen,
        onRemoveOption,
        options,
        placeholder = 'Select',
        required,
        styles,
        tabSelectsValue = true,
        usesExtraFields,
        virtualized = true, // Enable virtualization for large lists (>100 items)
    } = props;

    // Merge custom components with VirtualizedMenuList when virtualized
    // Always include NoLastPassInput to prevent LastPass from injecting icons
    const mergedComponents = useMemo(() => {
        const base = {
            Input: NoLastPassInput,
            MenuPortal: ModalMenuPortal,
            ...components,
        };
        if (virtualized) {
            return {
                MenuList: VirtualizedMenuList,
                ...base,
            };
        }
        return base;
    }, [virtualized, components]);

    // The ref must survive state updates because menu opening schedules a
    // follow-up scroll after React Select has mounted its menu list.
    const selectRef = useRef<any>(null);

    useEffect(() => {
        return () => {
            // Read at cleanup time: Cancel/OK set the ref after this effect last ran.
            const doReset = doResetRef.current;
            if (doReset) cacheReset();
            else cacheSave({inputValue, hasAutofocusRun});
        }
    }, [cacheReset, cacheSave, hasAutofocusRun, inputValue]);

    // If the parent supplied no default at mount but a valid one arrives
    // later (e.g. TodayView's UserSelect waiting for users to load), adopt
    // it. Once `option` is truthy this is a no-op, so it doesn't fight
    // user selections.
    useEffect(() => {
        if (defaultValue && !option) {
            setOption(defaultValue as SelectOption<T, S> | SelectOption<T, S>[]);
        }
    }, [defaultValue, option, setOption]);

    useImperativeHandle(ref, () => ({
        getOption() {
            if (option)
                return option as SelectOption<T, S>;
        },
        getOptions() {
            return option as unknown as SelectOption<T, S>[];
        },
        getValue() {
            return Array.isArray(option) ? null : option?.value;
        },
        getValues() {
            return Array.isArray(option) ? option.map(o => o.value) : null;
        },
        getData() {
            return Array.isArray(option) ? option.map(o => o.data) : option?.data;
        },
        ensureOpen() {
            // Ensure that the dropdown is open
            if (!isMenuOpen) setMenuOpen(true);
        },
    }), [isMenuOpen, option, setMenuOpen]);

    const onChangeSingle = useCallback((_option: SelectOption<T, S>, actionMeta: ActionMeta<any>) => {
        if (actionMeta.action === 'remove-value') onRemoveOption?.(actionMeta.removedValues);

        setOption(_option, () => {
            if (onChange) {
                if (usesExtraFields) onChange(_option, actionMeta);
                else if (options && _option) {
                    const {label, value} = _option;

                    if (label === allowNoneText) onChange(null, actionMeta);
                    else onChange(value, actionMeta);
                }
                else onChange(null, actionMeta);

                // Controlled mode: update the option state again after the callback
                // Note: We do NOT call onChange again - it was already called above
                if (controlled) {
                    setOption(_option);
                }
            }

            setMenuOpen(false);

            if (autoAdvance && _option) {
                advanceFocusFrom(selectRef.current?.inputRef as HTMLElement);
            }
        });
    }, [onRemoveOption, onChange, usesExtraFields, options, allowNoneText, controlled, setOption, setMenuOpen, autoAdvance, selectRef]);

    const handleCreateOption = useCallback((inputValue: string) => {
        onCreateOption(inputValue);

        if (autoAdvance) {
            // Auto-advance to next field after creating an option
            setTimeout(() => advanceFocusFrom(selectRef.current?.inputRef as HTMLElement), 150);
        } else {
            // Keep focus in the component after creating an option
            setTimeout(() => pWaitFor(() => !!selectRef.current, {timeout: 3000, signal: globalAbortSignal})
                .then(() => {
                    if (selectRef.current) {
                        // Focus the input element to keep the cursor visible
                        const inputElement = selectRef.current.inputRef as HTMLInputElement;
                        if (inputElement) {
                            inputElement.focus();
                            inputElement.setSelectionRange(inputValue.length, inputValue.length);
                        }
                    }
                })
                .catch(console.error), 100);
        }
    }, [autoAdvance, onCreateOption, selectRef]);

    const onChangeMulti = useCallback((options: SelectOption<T, S>[], actionMeta: ActionMeta<any>) => {
        if (actionMeta.action === 'remove-value') onRemoveOption?.(actionMeta.removedValues);
        if (onChange) onChange(options || [], actionMeta);
        setMenuOpen(false);
    }, [onRemoveOption, onChange, setMenuOpen]);

    const handleBlur: FocusEventHandler<HTMLInputElement> = useCallback(e => {
        setMenuOpen(false);

        props.onBlur?.(e);
    }, [props, setMenuOpen]);

    const handleFocus: FocusEventHandler<HTMLInputElement> = useCallback(e => {
        // Load the cache (but NOT isMenuOpen - let handleMenuOpen control that)
        // Reading isMenuOpen from cache can cause the menu to close immediately
        // if the cache has isMenuOpen: false from a previous blur.
        // `option` is also NOT loaded — the parent owns it via defaultValue.
        setInputValue(cacheValue('inputValue', ''));

        setHasAutofocusRun(true)

        // Call prop handle
        props.onFocus?.(e);
    }, [cacheValue, props]);

    const handleInputChange = useCallback((newValue: string, actionMeta: InputActionMeta) => {
        setInputValue(newValue);

        if (!newValue) props.onEmptyInput?.();
        props.onInputChange?.(newValue, actionMeta);
    }, [props, setInputValue]);

    /**
     * Close the menu if we are outside the menu when scrolling.
     *
     * @param {Event} event
     * @returns {boolean}
     */
    const shouldScrollClose = useCallback((event: Event): boolean => {
        // @ts-ignore
        return !event.target?.className?.startsWith('react-select');
    }, []);

    const customStyles = {
        input: (base: CSSProperties) => ({
            ...base,
            autoComplete: "off",
        }),
        placeholder: (base: CSSProperties, _: any) => ({
            ...base,
            textOverflow: 'ellipsis',
            overflowX: 'hidden',
            whiteSpace: 'nowrap',
            width: '100%',
        }),
        // Inside a dialog the menu shares the dialog's stacking context (ModalMenuPortal, OWA-955);
        // keep it above positioned dialog content such as a hovered Select (z-index 1003).
        // OWA-988: the room from the control's left edge to the screen's right edge (less a
        // gutter and any page scrollbar) caps the menu's width below. `rect` is the control's
        // viewport position. The cap is relative to 100vw so it follows a window resized while
        // the menu is open, which re-renders this only when the control moves or resizes.
        menuPortal: (base: CSSProperties, state: {rect?: {left: number, width: number}}) => ({
            ...base,
            zIndex: 1400,
            ...(state.rect && {
                '--select-menu-max-width': `max(${state.rect.width}px, calc(100vw - ${state.rect.left + MENU_GUTTER + window.innerWidth - document.documentElement.clientWidth}px))`,
            }),
        }),
        menuList: (base: CSSProperties) => ({
            ...base,
            zIndex: 100,  // Was 1000, setting to be in line with @react-select-z-index
        }),
        // A fixed value stays in a multi select; the parent still has to refuse Backspace (OWA-1581).
        multiValueRemove: (base: CSSObjectWithLabel, state: {data?: {isFixed?: boolean}}) =>
            state.data?.isFixed ? {...base, display: 'none'} : base,
        // OWA-988: as wide as the longest option and never narrower than the control. Options
        // wrap only where the menu would pass the screen edge. An inline (unportaled) menu has
        // no cap set and stays the control's width, so its container cannot clip it.
        menu: (base: CSSProperties) => ({
            ...base,
            zIndex: 100,  // Was 1000, setting to be in line with @react-select-z-index
            width: 'max-content',
            minWidth: '100%',
            maxWidth: 'var(--select-menu-max-width, 100%)',
        }),
        option: (base: CSSProperties) => ({
            ...base,
            zIndex: 1,
            height: 'fit-content'
        }),
    };

    const _styles = Object.assign(customStyles, styles);

    // Undefined, not null: React Select keeps a pick itself when the parent does not pass one back.
    const _defaultValue = defaultValue
                          ? defaultValue
                          : allowNone
                            ? allowNoneText
                            : undefined;

    const SelectClass = allowCreate ? CreatableSelect : ReactSelect;
    const classNames = ["Select", clsName, required ? 'required' : ''].join(' ').trim();

    const filterConfig: Config<any> = {
        ignoreCase: true,
        ignoreAccents: true,
        trim: true,
        matchFrom: 'any'
    };

    const value = controlled ? option : _defaultValue;

    /**
     * Keep an option visible by moving only this Select instance's menu list.
     * Scrolling an option with Element.scrollIntoView can also move a task card
     * ancestor, which React Select interprets as menu dismissal.
     *
     * @param selector Select option selector scoped to this menu list.
     * @param fallback Option ref supplied by React Select when it is rendered.
     */
    const scrollOptionWithinMenu = useCallback((selector: string, fallback?: HTMLElement | null) => {
        const menuList = selectRef.current?.menuListRef as HTMLElement | null;
        if (!menuList) return;

        const option = menuList.querySelector(selector) as HTMLElement | null ?? fallback;
        if (!option) return;

        // Virtualized rows sit in an absolutely positioned wrapper, making
        // offsetTop relative to that wrapper rather than the scrolling list.
        const menuListRect = menuList.getBoundingClientRect();
        const optionRect = option.getBoundingClientRect();
        const optionTop = optionRect.top - menuListRect.top + menuList.scrollTop;
        const optionBottom = optionTop + option.clientHeight;
        const menuBottom = menuList.scrollTop + menuList.clientHeight;

        // Match scrollIntoView({block: 'nearest'}): leave an already visible
        // option in place and otherwise move only far enough to reveal it.
        if (optionTop < menuList.scrollTop) {
            // eslint-disable-next-line react-hooks/immutability -- This event-frame callback scrolls the DOM element exposed by React Select, outside render.
            menuList.scrollTop = optionTop;
        } else if (optionBottom > menuBottom) {
            // eslint-disable-next-line react-hooks/immutability -- This event-frame callback scrolls the DOM element exposed by React Select, outside render.
            menuList.scrollTop = optionBottom - menuList.clientHeight;
        }
    }, []);

    const scrollSelectedOptionWithinMenu = useCallback(() => {
        // Apple browsers intentionally omit aria-selected. A multi-select can
        // also hide its selected rows, so fall back to React Select's focused
        // row when the selected row is not currently rendered.
        scrollOptionWithinMenu(
            '.react-select__option--is-selected, [aria-selected="true"]',
            selectRef.current?.focusedOptionRef as HTMLElement | null,
        );
    }, [scrollOptionWithinMenu]);

    const scrollFocusedOptionWithinMenu = useCallback(() => {
        scrollOptionWithinMenu(
            '.react-select__option--is-focused',
            selectRef.current?.focusedOptionRef as HTMLElement | null,
        );
    }, [scrollOptionWithinMenu]);

    const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(e => {
        if (e.key === 'Enter' || e.key === 'Tab') {
            doResetRef.current = true;
            cacheReset();
        }
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            setMenuOpen(true);
        }
        if (['ArrowDown', 'ArrowUp', 'PageUp', 'PageDown', 'Home', 'End'].includes(e.key)) {
            // React Select updates its focused option while handling this
            // event. Run after its commit but before paint so virtualized rows
            // can be measured without showing its intermediate scroll.
            requestAnimationFrame(scrollFocusedOptionWithinMenu);
        }
        if (e.key === 'Escape') setMenuOpen(false);
    }, [cacheReset, scrollFocusedOptionWithinMenu, setMenuOpen]);

    const handleMenuOpen = useCallback(() => {
        setMenuOpen(true);
        requestAnimationFrame(scrollSelectedOptionWithinMenu);
        onMenuOpen?.();
    }, [onMenuOpen, scrollSelectedOptionWithinMenu, setMenuOpen]);

    const handleMenuClose = useCallback(() => {
        setMenuOpen(false);
    }, [setMenuOpen]);

    const select = <SelectClass key={keyName}
                                aria-label={props.name}
                                autoFocus={hasAutofocusRun ? false : autoFocus}
                                backspaceRemovesValue={backspaceRemovesValue}
                                blurInputOnSelect={false}   // Don't blur input when selecting
                                captureMenuScroll={true}
                                className={classNames}
                                classNamePrefix="react-select"
                                closeMenuOnScroll={shouldScrollClose}
                                closeMenuOnSelect={true}
                                components={mergedComponents}
                                defaultValue={_defaultValue}
                                filterOption={filterOption || createFilter(filterConfig)}
                                formatCreateLabel={formatCreateLabel}
                                getNewOptionData={getNewOptionData}
                                inputValue={inputValue || ''}
                                isClearable={isClearable && allowNone}
                                isDisabled={disabled}
                                isMulti={isMulti}
                                isSearchable={props.isSearchable ?? true}
                                isValidNewOption={isValidNewOption}
                                menuIsOpen={props.menuIsOpen ?? isMenuOpen}
                                menuPlacement={menuPlacement}
                                menuPortalTarget={props.disablePortaling ? undefined : document.getElementById(props.menuPortalTarget || 'portal-select')}
                                menuPosition={props.disablePortaling ? 'absolute' : 'fixed'}
                                menuShouldBlockScroll={false}
                                // Inline menus live inside scrollable cards. Letting React Select
                                // scroll an ancestor to make room causes closeMenuOnScroll to
                                // treat that programmatic card scroll as user dismissal.
                                menuShouldScrollIntoView={!props.disablePortaling}
                                onBlur={handleBlur}
                                onChange={isMulti ? onChangeMulti : onChangeSingle}
                                onCreateOption={handleCreateOption}
                                onFocus={handleFocus}
                                onKeyDown={handleKeyDown}
                                onInputChange={handleInputChange}
                                onMenuClose={handleMenuClose}
                                onMenuOpen={handleMenuOpen}
                                options={options}
                                placeholder={placeholder}
                                ref={selectRef}
                                styles={_styles as StylesConfig<any, boolean, GroupBase<any>>}
                                tabSelectsValue={tabSelectsValue}
                                value={value}
    />;

    return encrypted ? <div className='Select--private'
                            data-private={encrypted}
    >{select}</div> : select;

});

export interface SelectHandle<T, S> {
    // If single-select, returns an option
    getOption: () => SelectOption<T, S>;

    // If multi-select, returns array of options
    getOptions: () => SelectOption<T, S>[];

    // If single-select, returns the currently selected value
    getValue: () => T,

    // If multi-select, returns array of values
    getValues: () => T[],

    // Returns the data associated with the selected option
    getData: () => S | S[],

    // Returns the selected option
    ensureOpen: () => void;
}

interface Config<Option> {
    readonly ignoreCase?: boolean,
    readonly ignoreAccents?: boolean,
    readonly stringify?: (option: FilterOptionOption<Option>) => string,
    readonly trim?: boolean,
    readonly matchFrom?: "any" | "start"
}

export type SelectDefault<T, S> = SelectOption<T, S> | SelectOption<T, S>[];

export type SelectPlaceholder = string | number | ReactNode;

export interface Accessors<Option> {
    getOptionValue: GetOptionValue<Option>;
    getOptionLabel: GetOptionLabel<Option>;
}

export interface FilterOptionOption<Option> {
    readonly label: string,
    readonly value: string,
    readonly data: Option,
}

export type SelectFilterOptionFunc = (option: FilterOptionOption<any>, inputValue: string) => boolean;
export type SelectIsValidNewOptionFunc = (inputValue: string, value: Options<any>, options: OptionsOrGroups<any, GroupBase<any>>, accessors: Accessors<any>) => boolean;
export type SelectGetNewOptionDataFunc = (inputValue: string, optionLabel: ReactNode) => any;
export type SelectOnChangeFunc = (newValue: any | any[], actionMeta: ActionMeta<any>) => void;
export type SelectOnInputChangeFunc = (newValue: string, actionMeta: InputActionMeta) => void;
export type SelectOnCreateOptionFunc = (inputValue: string) => void;
export type SelectOnRemoveOptionFunc = (inputValues: any | any[]) => void;
export type SelectFormatCreateLabelFunc = (inputValue: string) => ReactNode;

export type SelectProps<T, S> = {
    allowCreate?: boolean,
    allowNone?: boolean,
    allowNoneText?: string,
    /** After a single-select value is picked, advance focus to the next form field */
    autoAdvance?: boolean,
    autoFocus?: boolean,
    backspaceRemovesValue?: boolean,
    clsName?: string,
    controlled?: boolean,
    components?: any,
    defaultValue?: SelectDefault<T, S>,
    /** When true, renders menu inline instead of in a portal (useful in modals) */
    disablePortaling?: boolean,
    disabled?: boolean,
    encrypted?: boolean
    filterOption?: SelectFilterOptionFunc,
    formatCreateLabel?: SelectFormatCreateLabelFunc,
    getNewOptionData?: SelectGetNewOptionDataFunc,
    isClearable?: boolean,
    isMulti?: boolean,
    isSearchable?: boolean,
    isValidNewOption?: SelectIsValidNewOptionFunc,
    keyName?: string,
    menuIsOpen?: boolean,
    /** Menu placement: 'auto' (default), 'bottom', or 'top'. Use 'bottom' in modals to prevent clipping. */
    menuPlacement?: 'auto' | 'bottom' | 'top',
    menuPortalTarget?: string,
    name: string,
    onBlur?: FocusEventHandler<HTMLInputElement>,
    onChange?: SelectOnChangeFunc,
    onCreateOption?: SelectOnCreateOptionFunc,
    onFocus?: FocusEventHandler<HTMLInputElement>,
    onEmptyInput?: () => void,
    onInputChange?: SelectOnInputChangeFunc,
    onMenuOpen?: VoidFunction,
    onRemoveOption?: SelectOnRemoveOptionFunc,
    options: SelectOption<T, S>[] | GroupedSelectOption<T, S>[],
    optionType?: string,
    placeholder?: SelectPlaceholder,
    required?: boolean,
    styles?: object,
    tabSelectsValue?: boolean,
    usesExtraFields?: boolean,
    /** Enable virtualized rendering for large lists (default: true). Only virtualizes when >100 options. */
    virtualized?: boolean,
};

export default Select;
